import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from '@/contexts/ThemeContext'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useModerator } from '@/hooks/useModerator'
import { useAdmin } from '@/hooks/useAdmin'
import { useHeaderShowVersionFlag } from '@/hooks/useHeaderShowVersionFlag'
import logoSvg from '@/assets/sensorhub-logo.svg?raw'

export default function Header() {
  const location = useLocation()
  const { user } = useAuth()
  const { theme } = useTheme()
  const isModerator = useModerator()
  const isAdmin = useAdmin()

  // Fetch user's profile for display name
  const { data: profile } = useQuery({
    queryKey: ['profile-header', user?.id],
    queryFn: async () => {
      if (!user) return null
      const { data, error } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', user.id)
        .maybeSingle()

      if (error) {
        console.warn('Error fetching profile:', error)
        return null
      }
      return data
    },
    enabled: !!user,
    staleTime: 60000, // Cache for 1 minute
  })

  const { headerShowVersion } = useHeaderShowVersionFlag()
  const showVersion =
    (isModerator || isAdmin) && headerShowVersion
  const gitCommit = import.meta.env.VITE_GIT_COMMIT || ''
  const gitCommitDate = import.meta.env.VITE_GIT_COMMIT_DATE || ''
  const versionText =
    gitCommit && gitCommitDate
      ? `${gitCommit.slice(0, 7)} · ${new Date(gitCommitDate.trim()).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
      : gitCommit
        ? gitCommit.slice(0, 7)
        : ''

  // Logo fill colour from user theme (SVG_COLOUR_CONTROL: wrapper sets --pcp-brand-logo-fill; global CSS applies to path)
  const themeToFill: Record<string, string> = {
    light: '#000000',
    dark: '#FFFFFF',
    'yellow-black': '#FFFF00',
  }
  const logoFill = themeToFill[theme] ?? '#000000'

  return (
    <header 
      className="border-b-2"
      style={{ borderColor: 'var(--bg-tertiary)' }}
    >
      <nav className="container mx-auto px-4 py-4" aria-label="Main navigation">
        <div className="flex items-center justify-between">
          <div className="flex flex-col items-start">
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="pcp-brand-logo-fill flex items-center"
                style={{ '--pcp-brand-logo-fill': logoFill, height: '3.5rem', width: 'auto', minWidth: '2.75rem' } as React.CSSProperties}
              >
                <span className="sr-only">InsideAccess home</span>
                <div
                  style={{ width: '100%', height: '100%' }}
                  aria-hidden
                  dangerouslySetInnerHTML={{ __html: logoSvg }}
                />
              </Link>
              <Link
                to="/"
                className="text-xl font-semibold no-underline"
                style={{ color: 'var(--text-primary)' }}
              >
                InsideAccess
              </Link>
            </div>
            {showVersion && versionText && (
              <span
                className="mt-0.5 block text-[0.65rem]"
                style={{ color: 'var(--text-secondary)' }}
                aria-label={`Version: ${versionText}`}
              >
                {versionText}
              </span>
            )}
          </div>
          
          <ul className="flex gap-1 list-none">
            <li>
              <Link
                to="/"
                className="px-2 py-2"
                style={{
                  color: 'var(--text-primary)',
                  borderBottom: location.pathname === '/' ? '3px solid var(--color-focus)' : 'none',
                }}
                aria-current={location.pathname === '/' ? 'page' : undefined}
              >
                Home
              </Link>
            </li>
            {user ? (
              <>
                <li>
                  <Link
                    to="/activity"
                    className="px-2 py-2"
                    style={{
                      color: 'var(--text-primary)',
                      borderBottom: location.pathname === '/activity' ? '3px solid var(--color-focus)' : 'none',
                    }}
                    aria-current={location.pathname === '/activity' ? 'page' : undefined}
                  >
                    Activity
                  </Link>
                </li>
                <li>
                  <Link
                    to="/groups"
                    className="px-2 py-2"
                    style={{
                      color: 'var(--text-primary)',
                      borderBottom: location.pathname.startsWith('/groups') ? '3px solid var(--color-focus)' : 'none',
                    }}
                    aria-current={location.pathname.startsWith('/groups') ? 'page' : undefined}
                  >
                    Groups
                  </Link>
                </li>
                <li>
                  <Link
                    to="/links"
                    className="px-2 py-2"
                    style={{
                      color: 'var(--text-primary)',
                      borderBottom: location.pathname === '/links' ? '3px solid var(--color-focus)' : 'none',
                    }}
                    aria-current={location.pathname === '/links' ? 'page' : undefined}
                  >
                    Resources
                  </Link>
                </li>
                <li>
                  <Link
                    to="/tv-guide"
                    className="px-2 py-2"
                    style={{
                      color: 'var(--text-primary)',
                      borderBottom: location.pathname === '/tv-guide' ? '3px solid var(--color-focus)' : 'none',
                    }}
                    aria-current={location.pathname === '/tv-guide' ? 'page' : undefined}
                  >
                    TV Guide
                  </Link>
                </li>
                <li>
                  <Link
                    to="/streaming"
                    className="px-2 py-2"
                    style={{
                      color: 'var(--text-primary)',
                      borderBottom: location.pathname === '/streaming' ? '3px solid var(--color-focus)' : 'none',
                    }}
                    aria-current={location.pathname === '/streaming' ? 'page' : undefined}
                  >
                    Streaming
                  </Link>
                </li>
                {isModerator && (
                  <>
                    <li>
                      <Link
                        to="/uploads"
                        className="px-2 py-2"
                        style={{
                          color: 'var(--text-primary)',
                          borderBottom: location.pathname === '/uploads' ? '3px solid var(--color-focus)' : 'none',
                        }}
                        aria-current={location.pathname === '/uploads' ? 'page' : undefined}
                      >
                        Uploads
                      </Link>
                    </li>
                    {(isAdmin || isModerator) && (
                      <li>
                        <Link
                          to="/admin"
                          className="px-2 py-2"
                          style={{
                            color: 'var(--text-primary)',
                            borderBottom: location.pathname.startsWith('/admin') ? '3px solid var(--color-focus)' : 'none',
                          }}
                          aria-current={location.pathname.startsWith('/admin') ? 'page' : undefined}
                        >
                          Admin portal
                        </Link>
                      </li>
                    )}
                  </>
                )}
                <li>
                  <Link
                    to="/search"
                    className="px-2 py-2"
                    style={{
                      color: 'var(--text-primary)',
                      borderBottom: location.pathname === '/search' ? '3px solid var(--color-focus)' : 'none',
                    }}
                    aria-current={location.pathname === '/search' ? 'page' : undefined}
                  >
                    Search
                  </Link>
                </li>
                <li>
                  <Link
                    to="/profile"
                    className="px-2 py-2 text-lg font-bold rounded block"
                    style={{ 
                      color: 'var(--text-inverse)',
                      backgroundColor: location.pathname === '/profile' 
                        ? 'var(--bg-interactive-hover)' 
                        : 'var(--bg-interactive)',
                      border: '2px solid var(--bg-interactive)',
                      textDecoration: 'none',
                    }}
                    onMouseEnter={(e) => {
                      if (location.pathname !== '/profile') {
                        e.currentTarget.style.backgroundColor = 'var(--bg-interactive-hover)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (location.pathname !== '/profile') {
                        e.currentTarget.style.backgroundColor = 'var(--bg-interactive)'
                      }
                    }}
                    aria-label={profile?.display_name ? `Logged in as ${profile.display_name}. Click to view profile.` : 'View profile'}
                    aria-current={location.pathname === '/profile' ? 'page' : undefined}
                  >
                    {profile?.display_name || user?.email?.split('@')[0] || 'Profile'}
                  </Link>
                </li>
              </>
            ) : (
              <>
                <li>
                  <Link
                    to="/login"
                    className="px-2 py-2"
                    style={{
                      color: 'var(--text-primary)',
                      borderBottom: location.pathname === '/login' ? '3px solid var(--color-focus)' : 'none',
                    }}
                    aria-current={location.pathname === '/login' ? 'page' : undefined}
                  >
                    Login
                  </Link>
                </li>
                <li>
                  <Link
                    to="/register"
                    className="px-2 py-2"
                    style={{
                      color: 'var(--text-primary)',
                      borderBottom: location.pathname === '/register' ? '3px solid var(--color-focus)' : 'none',
                    }}
                    aria-current={location.pathname === '/register' ? 'page' : undefined}
                  >
                    Register
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
      </nav>
    </header>
  )
}

